import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import Button from '../../components/Button';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import {
  createReport,
  getReport,
  getReportDownload,
  listReportTemplates,
  listReports,
  mapAnalyticsError,
  REPORT_ERRORS,
} from '../../api/reports';
import { formatReportDownloadBlob, triggerBlobDownload } from '../../utils/reportPdf';
import { formatShortRange, MAX_REPORT_DAYS } from './period';
import styles from './Analytics.module.css';

/** Contract: only RUNNING | READY | FAILED */
const PENDING_STATUSES = new Set(['RUNNING']);
const POLL_MS = 1500;

/** Fetch report file bytes; use dev proxy to avoid S3 CORS during local development. */
async function fetchReportFile(url) {
  if (!url) throw new Error('missing url');

  if (url.startsWith('blob:') || url.startsWith('data:')) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to read report file (${res.status})`);
    return res.blob();
  }

  if (process.env.NODE_ENV === 'development') {
    const proxyUrl = `/__report_dl?target=${encodeURIComponent(url)}`;
    const proxied = await fetch(proxyUrl);
    if (proxied.ok) return proxied.blob();
  }

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to download report file (${res.status})`);
  return res.blob();
}

function mergeJob(prevJobs, updated) {
  if (!updated?.id) return prevJobs;
  const idx = prevJobs.findIndex((j) => j.id === updated.id);
  if (idx === -1) return [updated, ...prevJobs];
  const next = [...prevJobs];
  next[idx] = { ...next[idx], ...updated };
  return next;
}

function normalizeTemplates(raw, isAdmin) {
  const list = (raw?.templates || []).map((t) => ({
    type: t.type,
    label: t.label || t.type,
    admin_only: Boolean(t.admin_only),
  }));
  return list.filter((t) => isAdmin || !t.admin_only);
}

/** Shared report generate + history state for the Analytics toolbar and jobs list. */
export function useReports(isAdmin) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [templateType, setTemplateType] = useState('');
  const [format, setFormat] = useState('csv');
  const [jobs, setJobs] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState('');
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  /** Job ids the user generated as PDF (API often only emits CSV). */
  const pdfIntentRef = useRef(new Set());

  const labelByType = useMemo(() => {
    const map = Object.create(null);
    templates.forEach((t) => {
      map[t.type] = t.label;
    });
    return map;
  }, [templates]);

  const selected = useMemo(
    () => templates.find((t) => t.type === templateType) || templates[0],
    [templates, templateType]
  );

  const pendingKey = useMemo(
    () =>
      jobs
        .filter((j) => PENDING_STATUSES.has(j.status))
        .map((j) => j.id)
        .sort()
        .join(','),
    [jobs]
  );

  const loadTemplates = useCallback(async () => {
    try {
      const data = await listReportTemplates(user.idToken, { isAdmin });
      const list = normalizeTemplates(data, isAdmin);
      setTemplates(list);
      setTemplateType((prev) => {
        if (prev && list.some((t) => t.type === prev)) return prev;
        return list[0]?.type || '';
      });
    } catch (err) {
      setError(mapAnalyticsError(err));
    }
  }, [user.idToken, isAdmin]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    setError('');
    try {
      // Single page only — no cursor. limit default 20, max 50.
      const data = await listReports({ limit: 20 }, user.idToken);
      setJobs(data.reports || []);
    } catch (err) {
      setError(mapAnalyticsError(err));
    } finally {
      setLoadingHistory(false);
    }
  }, [user.idToken]);

  useEffect(() => {
    loadTemplates();
    loadHistory();
  }, [loadTemplates, loadHistory]);

  // Poll each in-flight job via GET /store/reports/{id}
  useEffect(() => {
    if (!pendingKey) return undefined;

    let cancelled = false;

    const pollOnce = async () => {
      const ids = jobsRef.current
        .filter((j) => PENDING_STATUSES.has(j.status))
        .map((j) => j.id);
      if (!ids.length) return;

      const results = await Promise.allSettled(ids.map((id) => getReport(id, user.idToken)));
      if (cancelled) return;

      setJobs((prev) => {
        let next = prev;
        let changed = false;
        results.forEach((result) => {
          if (result.status !== 'fulfilled' || !result.value) return;
          const updated = result.value.report;
          if (!updated) return;
          const merged = mergeJob(next, updated);
          if (merged !== next) {
            next = merged;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };

    pollOnce();
    const t = setInterval(pollOnce, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [pendingKey, user.idToken]);

  const generate = async ({ from, to, channel }) => {
    if (!selected || generating) return;
    setGenerating(true);
    setError('');
    try {
      const uiFormat = format;
      // Backend "PDF" is often raw CSV text. Request CSV, then format a real table PDF on download.
      const apiFormat = uiFormat === 'pdf' ? 'csv' : uiFormat;
      const created = await createReport(
        {
          type: selected.type,
          from,
          to,
          channel,
          format: apiFormat,
        },
        user.idToken
      );
      const job = created?.report;
      if (job?.id) {
        if (uiFormat === 'pdf') pdfIntentRef.current.add(job.id);
        setJobs((prev) =>
          mergeJob(prev.filter((j) => j.id !== job.id), {
            ...job,
            format: uiFormat,
          })
        );
      } else {
        await loadHistory();
      }
    } catch (err) {
      setError(mapAnalyticsError(err, { maxReportDays: MAX_REPORT_DAYS }));
    } finally {
      setGenerating(false);
    }
  };

  const download = async (id) => {
    setDownloadingId(id);
    setError('');
    try {
      const data = await getReportDownload(id, user.idToken);
      // Contract: always { download_url, expires_at } (+ content_type when READY). No filename.
      const url = data?.download_url;
      if (!url) {
        setError('Download link was not returned for this report.');
        return;
      }

      const job = jobsRef.current.find((j) => j.id === id);
      const wantsPdf =
        pdfIntentRef.current.has(id) ||
        String(data?.content_type || '').includes('pdf') ||
        String(job?.format || '').toLowerCase() === 'pdf';

      if (wantsPdf) {
        try {
          const source = await fetchReportFile(url);
          const title = labelByType[job?.type] || job?.type || 'Report';
          const subtitle = [formatShortRange(job?.from, job?.to), job?.channel]
            .filter(Boolean)
            .join(' · ');
          const formatted = await formatReportDownloadBlob(source, {
            title,
            subtitle,
            forcePdf: true,
          });
          if (formatted) {
            triggerBlobDownload(formatted, `report_${id}.pdf`);
            return;
          }
          setError('Could not build a table PDF from this report file.');
        } catch (err) {
          setError(
            err?.message ||
              'Could not read the report file to build a PDF. Restart the dev server if you just pulled this change.'
          );
          return;
        }
      }

      // CSV (or unformatted fallback): open/download the original file.
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener noreferrer';
      link.target = '_blank';
      if (url.startsWith('blob:')) {
        link.download = `report_${id}.csv`;
      }
      document.body.appendChild(link);
      link.click();
      link.remove();

      if (url.startsWith('blob:')) {
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (err) {
      if (err.status === 409 || err.code === 'not_ready') {
        setError(REPORT_ERRORS.not_ready);
      } else {
        setError(mapAnalyticsError(err));
      }
    } finally {
      setDownloadingId('');
    }
  };

  return {
    templates,
    selected,
    templateType,
    setTemplateType,
    labelByType,
    format,
    setFormat,
    jobs,
    loadingHistory,
    generating,
    error,
    downloadingId,
    loadHistory,
    generate,
    download,
  };
}

/** Compact generate controls for the Analytics filter bar. */
export function ReportsToolbar({ reports, from, to, channel, rangeTooLong = false, dayCount }) {
  const {
    templates,
    selected,
    templateType,
    setTemplateType,
    format,
    setFormat,
    generating,
    generate,
  } = reports;

  return (
    <div className={styles.reportToolbarWrap}>
      <div className={styles.reportToolbar}>
        <div className={styles.controlBlock}>
          <span className={styles.controlLabel}>Report</span>
          <select
            className={styles.reportSelect}
            value={templateType || selected?.type || ''}
            onChange={(e) => setTemplateType(e.target.value)}
            aria-label="Report type"
          >
            {templates.map((t) => (
              <option key={t.type} value={t.type}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.controlBlock}>
          <span className={styles.controlLabel}>Format</span>
          <div className={styles.segments}>
            <button
              type="button"
              className={`${styles.segment} ${format === 'csv' ? styles.segmentActive : ''}`}
              onClick={() => setFormat('csv')}
            >
              CSV
            </button>
            <button
              type="button"
              className={`${styles.segment} ${format === 'pdf' ? styles.segmentActive : ''}`}
              onClick={() => setFormat('pdf')}
            >
              PDF
            </button>
          </div>
        </div>

        <div className={styles.controlBlock}>
          <span className={styles.controlLabel} aria-hidden="true">
            &nbsp;
          </span>
          <Button
            className={styles.reportGenerateBtn}
            size="sm"
            onClick={() => generate({ from, to, channel })}
            disabled={generating || !selected || rangeTooLong}
            title={
              rangeTooLong
                ? `Reports support at most ${MAX_REPORT_DAYS} inclusive days`
                : undefined
            }
          >
            {generating ? (
              <>
                <Loader2 size={14} className={styles.spin} /> Generating…
              </>
            ) : (
              'Generate'
            )}
          </Button>
        </div>
      </div>

      {rangeTooLong && (
        <p className={styles.reportRangeWarn} role="status">
          Selected range is {dayCount} inclusive days. Reports allow at most {MAX_REPORT_DAYS}{' '}
          days — narrow the dates to generate.
        </p>
      )}
    </div>
  );
}

/** Recent report jobs — full-width section under the dashboard. */
export function ReportsHistory({ reports, mode = 'latest', emptyMessage }) {
  const {
    jobs,
    loadingHistory,
    error,
    downloadingId,
    download,
    labelByType,
  } = reports;

  const visibleJobs = mode === 'latest' ? jobs.slice(0, 1) : jobs;
  const showPreviousLink = mode === 'latest';

  return (
    <section className={styles.jobsSection}>
      {mode === 'latest' && (
        <div className={styles.widgetHead}>
          <h2 className={styles.widgetTitle}>Latest report</h2>
          {showPreviousLink && (
            <Link to="/analytics/reports" className={styles.viewPreviousBtn}>
              View previous reports
            </Link>
          )}
        </div>
      )}
      {error && <p className={styles.inlineError}>{error}</p>}
      {loadingHistory && jobs.length === 0 && <p className={styles.mutedText}>Loading…</p>}
      {!loadingHistory && jobs.length === 0 && !error && (
        <p className={styles.mutedText}>
          {emptyMessage
            || (mode === 'latest'
              ? 'No reports yet — generate one from the filters above.'
              : 'No previous reports yet.')}
        </p>
      )}
      <ul className={styles.jobList}>
        {visibleJobs.map((job) => (
          <li key={job.id} className={styles.jobRow}>
            <div className={styles.jobMeta}>
              <strong>{labelByType[job.type] || job.type}</strong>
              <span className={styles.mutedText}>
                {formatShortRange(job.from, job.to)} · {(job.format || '').toUpperCase()}
                {job.channel && job.channel !== 'ALL' ? ` · ${job.channel}` : ''}
              </span>
              <span className={styles.mutedText}>
                {job.created_at
                  ? new Date(job.created_at).toLocaleString('en-IN', {
                      timeZone: 'Asia/Kolkata',
                      day: 'numeric',
                      month: 'short',
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : ''}
              </span>
              {job.status === 'FAILED' && job.error && (
                <span className={styles.inlineError}>{job.error}</span>
              )}
            </div>
            <div className={styles.jobActions}>
              <StatusBadge status={job.status} size="sm" />
              {job.status === 'READY' && (
                <button
                  type="button"
                  className={styles.downloadLink}
                  onClick={() => download(job.id)}
                  disabled={downloadingId === job.id}
                >
                  <Download size={12} />
                  {downloadingId === job.id ? '…' : 'Download'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
